import React, { useMemo } from "react";
import { styled, ThemeProvider } from "styled-components";
import { debounce } from "throttle-debounce";
import media from "../../styles/mediaQueries";
import { Add, Subtract } from "../../icons";
import { Button } from "../Button";
import { genericInputFieldStyles, textInputThemes, } from "../../styles/sharedInputStyles";
import idGenerator from "../../utils/idGenerator";
import rem from "../../utils/rem";
const StyledStepperInputContainer = styled.div `
  display: flex;
  flex-direction: column;
  width: 100%;
`;
const StyledStepperInput = styled.div `
  display: flex;
  flex: 1;
  flex-direction: ${({ flexDir }) => (flexDir === "auto" ? "row" : flexDir)};
  justify-content: space-between;
  align-items: center;
  ${media.down("belowSmall") `
      ${({ flexDir }) => flexDir === "row"
    ? `
            align-items: flex-center;
          `
    : `
            flex-direction: column;
            align-items: flex-start;
        `};
  `}
`;
const StyledStepperInputLabel = styled.label `
  font-weight: 500;
  text-align: left;
  font-size: ${rem(14)};
  line-height: ${rem(20)};
  > div {
    margin-top: 0.3rem;
  }
  padding-right: 0.5rem;
  ${media.down("belowSmall") `
      ${({ flexDir }) => flexDir === "row"
    ? ""
    : `
          padding-bottom: 0.5rem;
          width: 100%;
          padding-right: 0;
          `};
  `}
`;
const StyledStepperContent = styled.div `
  font-size: ${rem(18)};
  line-height: ${rem(24)};
  font-weight: normal;
  padding-top: 2px;
  display: flex;
  flex-direction: row;
`;
const StyledStepperInputAction = styled.div `
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-left: 0.5rem;
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type="number"] {
    text-align: center;
    -moz-appearance: textfield;
    appearance: textField;
  }
  ${media.down("belowSmall") `

      ${({ direction }) => direction === "row"
    ? ""
    : `
            padding-top: 0.5rem;
            padding-left: 0;
          `};
  `}
`;
const StyledNumberInput = styled.input `
  ${genericInputFieldStyles}
  width: 64px;
  margin: 0 15px;
`;
const StyledNumberText = styled.span `
  min-width: 43px;
  text-align: center;
  font-size: ${rem(16)};
  font-weight: 500;
  padding: 2px 2px 0;
  line-height: 1;
`;
const FeedBackWrapper = styled.div `
  display: flex;
  width: 100%;
  justify-content: flex-end;
  padding-top: 0.5rem;
`;
export const StepperInput = ({ label, value, prefix, postfix, setValue, "data-cy": testId, min = 0, max = 100, price, allowZero = false, state = null, children, feedback, inputAsText = false, direction = "auto", debounceStepTimeMs, disabled, }) => {
    const isValid = () => {
        if (min !== 0 && allowZero && value === 0) {
            return "default";
        }
        if (value >= min && value <= max) {
            return "default";
        }
        return "error";
    };
    const currentState = !state ? isValid() : state;
    const themeVars = textInputThemes[currentState];
    const themeStateVars = { hasValue: !!value };
    const [inputId] = React.useState(() => idGenerator("fjordline-stepperinput-"));
    const addOneId = `addone-${inputId}`;
    const debounceStepFunc = useMemo(() => debounce(debounceStepTimeMs || 0, (newValue) => {
        setValue(newValue);
    }), [debounceStepTimeMs, setValue]);
    const debounceSetValue = (newValue) => debounceStepTimeMs && debounceStepTimeMs > 0
        ? debounceStepFunc(newValue)
        : setValue(newValue);
    const handleDown = () => debounceSetValue(allowZero && value === min ? 0 : value - 1);
    const handleUp = () => debounceSetValue(allowZero && value === 0 && min !== 0 ? min : value + 1);
    const subtractDisabled = () => allowZero && value > 0 ? false : value <= min;
    const handleChange = (event) => {
        try {
            const updateValue = +event.currentTarget.value;
            if (Number.isNaN(updateValue)) {
                throw Error("invalid number input");
            }
            /* if (allowZero && updateValue < value && value === min && updateValue >= 0) {
              return setValue(0)
            } */
            /* if (allowZero && updateValue > value && value === 0 && min !== 0) {
              return setValue(min)
            } */
            return debounceSetValue(updateValue);
        }
        catch (error) {
            // ignoring invalid input values(eg text/space)
            return null;
        }
    };
    const handleKey = (event) => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            event.stopPropagation();
        }
    };
    return (React.createElement(ThemeProvider, { theme: { vars: themeVars, state: themeStateVars } },
        React.createElement(StyledStepperInputContainer, { "data-cy": testId },
            React.createElement(StyledStepperInput, { flexDir: direction },
                React.createElement(StyledStepperInputLabel, { flexDir: direction, htmlFor: inputId },
                    label,
                    React.createElement(StyledStepperContent, null,
                        prefix && prefix,
                        price && (React.createElement(React.Fragment, null,
                            new Intl.NumberFormat(price.locale, {
                                style: "currency",
                                currency: price.currency,
                            }).format(price.amount),
                            " ",
                            price.postfix && price.postfix)),
                        postfix && postfix)),
                React.createElement(StyledStepperInputAction, { flexDir: direction },
                    React.createElement(Button, { type: "button", onKeyDown: handleKey, onClick: handleDown, disabled: subtractDisabled(), "aria-label": "subtract one", leadingIcon: Subtract, theme: "ghost" }),
                    inputAsText ? (React.createElement(StyledNumberText, null, value)) : (React.createElement(StyledNumberInput, { role: "spinbutton", "aria-valuemin": min, "aria-valuemax": max, "aria-valuenow": value, "aria-labelledby": inputId, id: inputId, disabled: disabled, type: "number", value: value.toString(), min: allowZero ? 0 : min, max: max, onChange: handleChange })),
                    React.createElement(Button, { onKeyDown: handleKey, onClick: handleUp, disabled: value >= max || disabled, "aria-label": "add one", leadingIcon: Add, theme: "ghost", "data-cy": addOneId }))),
            children && React.createElement(React.Fragment, null, children)),
        feedback && React.createElement(FeedBackWrapper, null, feedback)));
};
StepperInput.displayName = "StepperInput";
export default StepperInput;
//# sourceMappingURL=StepperInput.js.map